import { Component, h, Prop, Element } from '@stencil/core';
import { createRipple } from '../../utils/utils';

@Component({
  tag: 'signet-button',
  styleUrl: 'signet-button.scss',
  shadow: false,
})
export class SignetButton {
  /**
   * Three button choices are: primary, text and outline.
   */
  @Element() $el: HTMLElement;

  /**
   * Button type, can be 'button', 'submit', or 'reset'.
   */
  @Prop() type: 'button' | 'submit' | 'reset' = 'button';
  /**
   * Button style, can be 'primary', 'text', or 'outlined'.
   */
  @Prop() class: 'primary' | 'text' | 'outlined' = 'primary';
  /**
   * Indicates if the button controls a popup.
   */
  @Prop() haspopup = false;
  /**
   * ID of the element controlled by the button.
   */
  @Prop() controls: string;
  /**
   *ID of the element hidden by the button.
   */
  @Prop() hides: string;
  /**
   * ID of the element shown by the button.
   */
  @Prop() shows: string;
  /**
   * Indicates if the button is in an expanded state.
   */
  @Prop() expanded = false;
  /**
   * Additional class for the button.
   */
  @Prop() buttonClass = '';
  /**
   * Indicates if the button is disabled.
   */
  @Prop() disabled = false;

  /**
   * The `size` property determines the size of the select component.
   * It can be set to 'sm', 'md', 'lg', 'small', 'medium', or 'large' to adjust the appearance of the component accordingly.
   * The default size is 'md'.
   */

  @Prop() size: 'sm' | 'md' | 'lg' | 'small' | 'medium' | 'large' = 'md';

  private get normalizedSize(): 'sm' | 'md' | 'lg' {
    switch (this.size) {
      case 'small':
        return 'sm';
      case 'medium':
        return 'md';
      case 'large':
        return 'lg';
      case 'sm':
      case 'md':
      case 'lg':
      default:
        return this.size as 'sm' | 'md' | 'lg';
    }
  }

  componentDidRender() {
    this.setEventListeners();
  }

  private onClick(event) {
    createRipple(event.currentTarget, event);
  }

  private setEventListeners() {
    this.setOpensListener();
    this.setHidesListener();
  }

  private setOpensListener() {
    if (!this.shows) return;

    const $showsComponent = document.getElementById(
      this.shows
    ) as HTMLSignetOverlayElement;

    if (!$showsComponent) return;

    this.haspopup = true;
    this.controls = this.shows;
    this.$el.addEventListener('click', () => $showsComponent.show());
  }

  private setHidesListener() {
    if (!this.hides) return;

    const $hidesComponent = document.getElementById(
      this.hides
    ) as HTMLSignetOverlayElement;

    if (!$hidesComponent) return;

    this.haspopup = true;
    this.controls = this.hides;
    this.$el.addEventListener('click', () => $hidesComponent.hide());
  }

  render() {
    const classes = {
      'signet-button': true,
      [this.class]: true,
      [this.buttonClass]: true,
      [`signet-button--${this.normalizedSize}`]: true,
    };

    return (
      <button
        disabled={this.disabled}
        onClick={this.onClick}
        class={classes}
        type={this.type}
      >
        <slot />
      </button>
    );
  }
}
